import Layout from "../Layout";
import DashboardCard from "./DashboardCard";
import { AgCharts } from "ag-charts-react";
import { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../redux/store";
import {
  cardResultQuery,
  lineCytdResultQuery,
  linePytdResultQuery,
  newQuery,
  setupQuery,
  vioAsofDataResultQuery,
  vioResultQuery,
} from "../../../redux/dashboardSlice";
import { Query } from "../../../interfaces/queryBuilderInterfaces";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { Dropdown } from "../../../components/dropdown/Dropdown";
import Loading from "../../../components/Loading";
import {
  createBarChartOptions,
  createLineChartOptions,
  createLineSeries,
  createSeries,
  colorsList,
  createFormData,
  groupAndSumVehicleCounts,
  groupAndSumVehicleCountsVio,
  getCardData,
} from "./HomeUtils";
import { useNavigate } from "react-router-dom";
import { Cube, getCubes } from "../../../redux/queryMetaSlice";
import { MultiSelectDropdown } from "../../../components/dropdown/MultiSelectDropdown";
const VIN_OPTIONS = [
  { label: "1", value: "1" },
  { label: "2", value: "2" },
  { label: "3", value: "3" },
  { label: "4", value: "4" },
  { label: "5", value: "5" },
  { label: "6", value: "6" },
  { label: "7", value: "7" },
  { label: "8", value: "8" },
  { label: "P", value: "P" },
  // { label: "UNKNOWN", value: "UNKNOWN" },
];

type CubeDetail = {
  cube: string;
  schema: string;
};

const DEFAULT_NEW_REGISTRATION_CUBE_DETAIL: CubeDetail = {
  cube: "NVR_PLK",
  schema: "New Registration PLK",
};

const DEFAULT_VIO_CUBE_DETAIL: CubeDetail = {
  cube: "VIO_PLK",
  schema: "VIO PLK",
};

type CubeMetaLookup = {
  nameIncludes: string;
  country: string;
  loadType: string;
};

const normalizeText = (value: string = "") => value.toLowerCase();

const matchesNameAndCountry = (cube: any, lookup: CubeMetaLookup) => {
  const hasNameMatch = cube?.annotations["data.load.type"] === lookup.loadType;
  const hasCountryMatch = cube?.annotations?.categoryGroup === lookup.country;

  return hasNameMatch && hasCountryMatch;
};

const getCubeDetailFromMeta = (
  metaData: any,
  lookup: CubeMetaLookup,
  fallback: CubeDetail,
): CubeDetail => {
  if (!Array.isArray(metaData)) {
    return fallback;
  }

  for (const metaItem of metaData) {
    for (const catalog of metaItem.catalogs || []) {
      for (const schema of catalog.schemas || []) {
        const matchingCube = (schema.cubes || []).find((cube: any) =>
          matchesNameAndCountry(cube, lookup),
        );
        if (matchingCube) {
          return {
            cube: matchingCube.name,
            schema: matchingCube.schema || schema.name || catalog.name,
          };
        }
      }
    }
  }
  return fallback;
};

const getCubeLoadDateFromMeta = (
  metaData: any,
  lookup: CubeMetaLookup,
): string => {
  if (!Array.isArray(metaData)) {
    return "";
  }

  for (const metaItem of metaData) {
    for (const catalog of metaItem.catalogs || []) {
      for (const schema of catalog.schemas || []) {
        const matchingCube = (schema.cubes || []).find((cube: any) =>
          matchesNameAndCountry(cube, lookup),
        );

        const loadDate = matchingCube?.annotations?.["load.date"];
        if (loadDate) {
          return loadDate;
        }
      }
    }
  }

  return "";
};

const hasCubeInMeta = (metaData: any, lookup: CubeMetaLookup): boolean => {
  if (!Array.isArray(metaData)) {
    return false;
  }

  for (const metaItem of metaData) {
    for (const catalog of metaItem.catalogs || []) {
      for (const schema of catalog.schemas || []) {
        const matchingCube = (schema.cubes || []).find((cube: any) =>
          matchesNameAndCountry(cube, lookup),
        );

        if (matchingCube) {
          return true;
        }
      }
    }
  }

  return false;
};
export default function Home() {
  const dispatch = useDispatch<AppDispatch>();
  const [selectedVIN, setSelectedVIN] = useState<string[]>(VIN_OPTIONS.map(v => v.value));
  const [lineChartOptions, setLineChartOptions] = useState<any>(null);
  const [stackChartOptions, setStackChartOptions] = useState<any>(null);
  const [barGroupChartOptions, setBarGroupChartOptions] = useState<any[]>([]);

  const [dashboardCardData, setDashboardCardData] = useState<
    { key: string; label: string; value: number }[]
  >([]);
  const [rowDashboardCardData, setRowDashboardCardData] = useState([]);
  const [percentYtdDiff, setPercentYtdDiff] = useState<string>("");
  const [percentMtdDiff, setPercentMtdDiff] = useState<string>("");
  const [cytdChartData, setCytdChartData] = useState<any>(null);
  const [pytdChartData, setPytdChartData] = useState<any>(null);
  const [vioData, setVioData] = useState<any>(null);
  const [vioAsofData, setVioAsofDate] = useState<any>(null);
  const loadingIcon = <FontAwesomeIcon icon={faSpinner} spin />;
  const [selectedCategory, setSelectedCategory] = useState<string[]>();
  const [perfomanceMetricsCategory, setPerfomanceMetricsCategory] =
    useState(null);
  const cardResults = useSelector((state: any) => state.dashboard.cardResults);
  const cytdLineResults = useSelector(
    (state: any) => state.dashboard.cytdLineResults,
  );
  const pytdLineResults = useSelector(
    (state: any) => state.dashboard.pytdLineResults,
  );
  const vioResults = useSelector((state: any) => state.dashboard.vioResults);
  const vioDateResults = useSelector(
    (state: any) => state.dashboard.vioDateResults,
  );
  const meta = useSelector((state: any) => state.meta);
  const currentActiveTab = useSelector(
    (state: any) => state.queryBuilder.currentTabIndex,
  );

  const country = "US";
  const newRegistrationLookup = useMemo(
    () => ({ nameIncludes: "New Registration", country, loadType: "NEW" }),
    [country],
  );
  const vioLookup = useMemo(
    () => ({ nameIncludes: "VIO", country, loadType: "VIO" }),
    [country],
  );

  const newRegistrationCubeDetail = useMemo(
    () =>
      getCubeDetailFromMeta(
        meta.meta,
        newRegistrationLookup,
        DEFAULT_NEW_REGISTRATION_CUBE_DETAIL,
      ),
    [meta.meta, newRegistrationLookup],
  );
  const vioCubeDetail = useMemo(
    () => getCubeDetailFromMeta(meta.meta, vioLookup, DEFAULT_VIO_CUBE_DETAIL),
    [meta.meta, vioLookup],
  );
  const newFormData = useMemo(
    () => createFormData(newRegistrationCubeDetail),
    [newRegistrationCubeDetail],
  );

  const vioFormData = useMemo(
    () => createFormData(vioCubeDetail),
    [vioCubeDetail],
  );

  const hasVioCube = useMemo(
    () => hasCubeInMeta(meta.meta, vioLookup),
    [meta.meta, vioLookup],
  );

  const hasNewRegistrationCube = useMemo(
    () => hasCubeInMeta(meta.meta, newRegistrationLookup),
    [meta.meta, newRegistrationLookup],
  );

  const getDashboardCarddataTotal = (
    queryResult: any,
    selectedCategory: any,
  ) => {
    // All VIN GVW Classes
    // const selectedCategory =
    //   gvwCategories[selectedValue as GvwCategoryKey] || null;
    const queryTypes = ["CYTD", "PYTD", "MRMO", "MRMO1"];
    const cardData = queryTypes.map((queryType) => {
      const header =
        queryResult.headers.find((header: any) => header.field === queryType)
          ?.headerName || "";
      let value = 0;
      queryResult.rows.forEach((item: any) => {
        if (item[`${queryType}|[Vehicle Count]`]) {
          if (selectedCategory && selectedCategory.length > 0) {
            if (selectedCategory.includes(item["VIN GVW Class"])) {
              value = value + item[`${queryType}|[Vehicle Count]`];
            }
          } else {
            value = value + item[`${queryType}|[Vehicle Count]`];
          }
        }
      });
      // if (queryResult.rows[0][`${queryType}|[Vehicle Count]`]) {
      //     value = queryResult.rows[0][`${queryType}|[Vehicle Count]`];
      // }
      return { key: queryType, label: header, value };
    });
    setDashboardCardData(cardData);

    // let
  };

  const fetchDashCardData = async () => {
    const queryTypes = ["CYTD", "PYTD", "MRMO", "MRMO1"];
    const getAllCardsData = async (queryResult: any) => {
      setRowDashboardCardData(queryResult);
      getDashboardCarddataTotal(queryResult, []);
      // const cardData = queryTypes.map(queryType => {
      //     const header = queryResult.headers.find((header:any) => header.field === queryType)?.headerName || '';
      //     let value = 0;
      //     if (queryResult.rows[0][`${queryType}|[Vehicle Count]`]) {
      //         value = queryResult.rows[0][`${queryType}|[Vehicle Count]`];
      //     }
      //     return { key: queryType, label: header, value };
      // });
      // setDashboardCardData(cardData);
    };
    if (!cardResults && dashboardCardData.length === 0) {
      const newQueryResult = (await dispatch(newQuery(newFormData))) as any;
      const updatedQuery: Query = {
        ...newQueryResult.payload,
        axes: {
          COLUMNS: newQueryResult.payload.axes.COLUMNS,
          ROWS: [
            ...newQueryResult.payload.axes.ROWS,
            "[VehAttr].[VehAttr].[GVW]",
          ],
        },
        name: newQueryResult.payload.name,
        enhancedFilterCriteria: {
          [Object.keys(newQueryResult.payload.enhancedFilterCriteria)[0]]: [
            {
              filterCategory:
                newQueryResult.payload.enhancedFilterCriteria[
                  Object.keys(newQueryResult.payload.enhancedFilterCriteria)[0]
                ][0].filterCategory,
              criteria: [
                {
                  ...newQueryResult.payload.enhancedFilterCriteria[
                    Object.keys(
                      newQueryResult.payload.enhancedFilterCriteria,
                    )[0]
                  ][0].filterCategory[0].criteria,
                  values: [
                    ...queryTypes.map((type) => ({
                      selType: "None",
                      value: type,
                      namedSet: false,
                    })),
                  ],
                },
              ],
            },
          ],
        },
      };
      const resultQueryResult = (await dispatch(
        setupQuery(updatedQuery),
      )) as any;
      const result = (await dispatch(
        cardResultQuery(resultQueryResult.payload),
      )) as any;

      getAllCardsData(result.payload.result);
    } else {
      getAllCardsData(cardResults.result);
    }
  };

  const fetchVioData = async () => {
    if (!vioResults) {
      const newQueryResult = (await dispatch(newQuery(vioFormData))) as any;
      //add GVW level as a row axis
      const updatedQuery: Query = {
        ...newQueryResult.payload,
        name: newQueryResult.payload?.name,
        axes: {
          ...newQueryResult.payload?.axes,
          ROWS: ["[VehAttr].[VehAttr].[GVW]"],
        },
      };
      const resultQueryResult = (await dispatch(
        setupQuery(updatedQuery),
      )) as any;
      const result = (await dispatch(
        vioResultQuery(resultQueryResult.payload),
      )) as any;
      setVioData(result.payload.result);
    } else {
      setVioData(vioResults.result);
    }
  };

  const buildLineQuery = (
    basePayload: any,
    queryTypes: string[] = [],
    addEnhancedFilter: boolean = false,
  ): Query => {
    const axes = {
      ...basePayload.axes,
      ROWS: ["[VehAttr].[VehAttr].[GVW]", "[New Month].[New Month].[Month]"],
      COLUMNS: ["[Measures].[Vehicle Count]"],
    };
    let enhancedFilterCriteria = basePayload.enhancedFilterCriteria;
    if (addEnhancedFilter && queryTypes.length > 0) {
      enhancedFilterCriteria = {
        [Object.keys(basePayload.enhancedFilterCriteria)[0]]: [
          {
            filterCategory:
              basePayload.enhancedFilterCriteria[
                Object.keys(basePayload.enhancedFilterCriteria)[0]
              ][0].filterCategory,
            criteria: [
              {
                ...basePayload.enhancedFilterCriteria[
                  Object.keys(basePayload.enhancedFilterCriteria)[0]
                ][0].filterCategory[0].criteria,
                values: [
                  ...queryTypes.map((type) => ({
                    selType: "None",
                    value: type,
                    namedSet: false,
                  })),
                ],
              },
            ],
          },
        ],
      };
    }
    return {
      ...basePayload,
      name: basePayload.name,
      axes,
      ...(addEnhancedFilter ? { enhancedFilterCriteria } : {}),
    };
  };

  const fetchChartData = async () => {
    if (!cytdLineResults || !pytdLineResults) {
      const newQueryResult = (await dispatch(newQuery(newFormData))) as any;
      const queryTypes = ["MRCYE"];
      // CYTD Query
      const cytdQuery = buildLineQuery(newQueryResult.payload);
      let resultQueryResult = (await dispatch(setupQuery(cytdQuery))) as any;
      let result = (await dispatch(
        lineCytdResultQuery(resultQueryResult.payload),
      )) as any;
      setCytdChartData(result.payload.result);
      // PYTD Query
      const pytdQuery = buildLineQuery(
        newQueryResult.payload,
        queryTypes,
        true,
      );
      resultQueryResult = (await dispatch(setupQuery(pytdQuery))) as any;
      result = (await dispatch(
        linePytdResultQuery(resultQueryResult.payload),
      )) as any;
      setPytdChartData(result.payload.result);
    } else {
      setCytdChartData(cytdLineResults.result);
      setPytdChartData(pytdLineResults.result);
    }
  };

  useEffect(() => {
    dispatch(getCubes(currentActiveTab));
  }, [dispatch]);

  const fetchVioDateAsof = async () => {
    const date = getCubeLoadDateFromMeta(meta.meta, vioLookup);
    if (!date) {
      setVioAsofDate("");
      return;
    }

    const formattedDate = new Date(date)
      .toLocaleDateString("en-US", { month: "short", year: "numeric" })
      .replace(" ", "-");
    setVioAsofDate(formattedDate);
  };
  useEffect(() => {
    if (meta.meta && meta.meta.length > 0 && hasVioCube) {
      fetchVioDateAsof();
    } else {
      setVioAsofDate("");
    }
  }, [meta, dispatch, hasVioCube]);

  useEffect(() => {
    if (meta.meta && meta.meta.length > 0 && hasVioCube) {
      fetchVioData();
    } else {
      setVioData(null);
      setStackChartOptions(null);
      setBarGroupChartOptions([]);
    }
  }, [meta, dispatch, hasVioCube]);

  useEffect(() => {
    if (meta.meta && meta.meta.length > 0 && hasNewRegistrationCube) {
      fetchDashCardData();
    } else {
      setDashboardCardData([]);
      setRowDashboardCardData([]);
    }
  }, [dispatch, meta, hasNewRegistrationCube]);

  useEffect(() => {
    if (meta.meta && meta.meta.length > 0 && hasNewRegistrationCube) {
      fetchChartData();
    } else {
      setCytdChartData(null);
      setPytdChartData(null);
      setLineChartOptions(null);
    }
  }, [dispatch, meta, hasNewRegistrationCube]);

  const cytdData = useMemo(
    () => getCardData("CYTD", dashboardCardData),
    [dashboardCardData],
  );
  const pytdData = useMemo(
    () => getCardData("PYTD", dashboardCardData),
    [dashboardCardData],
  );
  const mrmoData = useMemo(
    () => getCardData("MRMO", dashboardCardData),
    [dashboardCardData],
  );
  const mrmo1Data = useMemo(
    () => getCardData("MRMO1", dashboardCardData),
    [dashboardCardData],
  );

  const calcPercDiff = (current: number, previous: number) => {
    return previous !== 0
      ? (((current - previous) / previous) * 100).toFixed(2) + "%"
      : "";
  };

  useEffect(() => {
    if (dashboardCardData.length > 0) {
      if (cytdData && pytdData) {
        const percentYtdDiff = calcPercDiff(cytdData.value, pytdData.value);
        setPercentYtdDiff(percentYtdDiff);
      }
      if (mrmoData && mrmo1Data) {
        const percentMtdDiff = calcPercDiff(mrmoData.value, mrmo1Data.value);
        setPercentMtdDiff(percentMtdDiff);
      }
    }
  }, [dashboardCardData]);

  type GvwCategoryKey =
    | "VIN GVW Class 1"
    | "VIN GVW Class 2"
    | "VIN GVW Class 3"
    | "VIN GVW Class 4"
    | "VIN GVW Class 5"
    | "VIN GVW Class 6"
    | "VIN GVW Class 7"
    | "VIN GVW Class 8"
    | "Passenger Cars"
    | "Unclassified";

  const gvwCategories: Record<GvwCategoryKey, string[]> = useMemo(
    () => ({
      "VIN GVW Class 1": ["1"],
      "VIN GVW Class 2": ["2"],
      "VIN GVW Class 3": ["3"],
      "VIN GVW Class 4": ["4"],
      "VIN GVW Class 5": ["5"],
      "VIN GVW Class 6": ["6"],
      "VIN GVW Class 7": ["7"],
      "VIN GVW Class 8": ["8"],
      "Passenger Cars": ["P"],
      Unclassified: ["UNKNOWN"],
    }),
    [],
  );

  const [dropdownOptions, setDropdownOptions] = useState(() => [
    { label: "All VIN GVW", value: "All VIN GVW Classes" },
    ...Object.keys(gvwCategories).map((key) => ({ label: key, value: key })),
  ]);

  const createLineChartData = (overallCounts: any, pytdOverallCounts: any) => {
    const cytdYearData = Object.entries(overallCounts).map(
      ([month, count]) => ({
        Month: month,
        "Vehicle Count": count,
      }),
    );
    const pytdYearData = Object.entries(pytdOverallCounts).map(
      ([month, count]) => ({
        Month: month,
        "Vehicle Count": count,
      }),
    );

    const getYearFromMonth = (monthString: any) => monthString?.split(" ")[1];
    const cytdYear = getYearFromMonth(
      cytdYearData.map((row: any) => row.Month)[0],
    );
    const pytdYear = getYearFromMonth(
      pytdYearData.map((row: any) => row.Month)[0],
    );

    const combinedData = cytdYearData
      .map((cytdRow: any) => {
        return {
          Month: cytdRow.Month.split(" ")[0].slice(0, 3),
          [cytdYear]: cytdRow["Vehicle Count"],
          [pytdYear]:
            pytdYearData.find(
              (pytdRow: any) =>
                pytdRow.Month.split(" ")[0] === cytdRow.Month.split(" ")[0],
            )?.["Vehicle Count"] || 0,
        };
      })
      .concat(
        pytdYearData
          .filter(
            (pytdRow: any) =>
              !cytdYearData.some(
                (cytdRow: any) =>
                  cytdRow.Month.split(" ")[0] === pytdRow.Month.split(" ")[0],
              ),
          )
          .map((pytdRow: any) => ({
            Month: pytdRow.Month.split(" ")[0].slice(0, 3),
            [pytdYear]: pytdRow["Vehicle Count"],
          })),
      );
    const series = createLineSeries([cytdYear, pytdYear]);
    const lineChart = createLineChartOptions(series, combinedData);
    setLineChartOptions(lineChart);
  };

  const handleDropdown = (selectedCategory: any) => {
    // const selectedCategory =
    //   gvwCategories[selectedValue as GvwCategoryKey] || null;
    setSelectedCategory(selectedCategory);
    if (cytdChartData?.rows && pytdChartData?.rows) {
      const filteredCytdData = groupAndSumVehicleCounts(
        cytdChartData.rows,
        selectedCategory,
      );
      const filteredPytdData = groupAndSumVehicleCounts(
        pytdChartData.rows,
        selectedCategory,
      );
      if (
        Object.keys(filteredCytdData).length > 0 &&
        Object.keys(filteredPytdData).length > 0
      ) {
        createLineChartData(filteredCytdData, filteredPytdData);
      }
    }
  };

  useEffect(() => {
    if (cytdChartData && pytdChartData) {
      const overallCounts = groupAndSumVehicleCounts(cytdChartData.rows, null);
      const pytdOverallCounts = groupAndSumVehicleCounts(
        pytdChartData.rows,
        null,
      );
      if (
        Object.keys(overallCounts).length > 0 &&
        Object.keys(pytdOverallCounts).length > 0
      ) {
        createLineChartData(overallCounts, pytdOverallCounts);
      }

      //Remove the dropdown option if no data for that category
      const availableCategories = Object.keys(gvwCategories).filter((key) => {
        const countsCytd = groupAndSumVehicleCounts(
          cytdChartData.rows,
          gvwCategories[key as GvwCategoryKey],
        );
        const countsPytd = groupAndSumVehicleCounts(
          pytdChartData.rows,
          gvwCategories[key as GvwCategoryKey],
        );

        return (
          Object.values(countsCytd).some((count) => (count as number) > 0) ||
          Object.values(countsPytd).some((count) => (count as number) > 0)
        );
      });

      setDropdownOptions([
        { label: "All VIN GVW", value: "All VIN GVW Classes" },
        ...availableCategories.map((key) => ({ label: key, value: key })),
      ]);
    }
  }, [cytdChartData, pytdChartData]);

  const calStackChartOptions = (vioData: any, categories: any) => {
    if (vioData) {
      const summedCounts = groupAndSumVehicleCountsVio(
        vioData.rows,
        gvwCategories,
      );

      // if data for yKey is 0, remove it from ykeys
      const yKeys = Object.keys(summedCounts).filter(
        (yKey: string) => summedCounts[yKey] > 0,
      );
      let series: any[] = [];
      const minHeight = 140;
      const maxHeight = Math.min(window.innerHeight * 0.45, 350);
      let barWidth: number;
      const isNarrowScreen = window.innerWidth < 1600;
      const baseWidth = isNarrowScreen ? 0.09 : 0.095;
      const maxBarWidth = isNarrowScreen
        ? yKeys.length > 4
          ? 20
          : 30
        : yKeys.length > 4
          ? 40
          : 60;
      barWidth = Math.min(
        window.innerWidth * baseWidth,
        yKeys.length * maxBarWidth,
      );

      yKeys.forEach((yKey: string, index: any) => {
        series.push({
          type: "bar",
          xKey: "category",
          normalizedTo: 100,

          yKey: yKey,
          stacked: true,
          tooltip: {
            renderer: (params: any) => {
              return `<div style="padding: 8px">
                           <b> ${params.yKey} </b> <br> ${params.datum[yKey].toLocaleString("en-US", { maximumFractionDigits: 0 })}</div>`;
            },
          },
          fill: colorsList[index % colorsList.length],
        });
      });
      let totalSum: any = (Object.values(summedCounts) as number[]).reduce(
        (acc: number, val: number) => acc + val,
        0,
      );
      const totalSumFormatted = totalSum.toLocaleString("en-US", {
        maximumFractionDigits: 0,
      });
      const data = [{ category: "Total Vehicles", ...summedCounts }];
      const stackChartOptions = createBarChartOptions(
        series,
        data,
        totalSumFormatted,
        "100.00",
        maxHeight,
        barWidth,
        totalSum,
        true,
      );
      setStackChartOptions(stackChartOptions);

      const newBarGroupOptions = yKeys.map((yKey, idx) => {
        const percent = (summedCounts[yKey] / totalSum) * 100;
        const height = minHeight + (maxHeight - minHeight) * (percent / 100);
        const data = [{ category: yKey, ...summedCounts }];
        return createBarChartOptions(
          createSeries(yKey, idx),
          data,
          summedCounts[yKey].toLocaleString("en-US", {
            maximumFractionDigits: 0,
          }),
          percent.toFixed(2),
          height,
          barWidth,
          totalSum,
          false,
        );
      });
      setBarGroupChartOptions(newBarGroupOptions);
    }
  };
  useEffect(() => {
    calStackChartOptions(vioData, gvwCategories);
  }, [vioData]);

  const navigate = useNavigate();
  if (location.pathname === "/") {
    navigate("/home");
  }

  useEffect(() => {
    const handleResize = () => {
      calStackChartOptions(vioData, gvwCategories);
      const overallCounts = groupAndSumVehicleCounts(
        cytdChartData?.rows,
        selectedCategory,
      );
      const pytdOverallCounts = groupAndSumVehicleCounts(
        pytdChartData?.rows,
        selectedCategory,
      );
      if (
        Object.keys(overallCounts).length > 0 &&
        Object.keys(pytdOverallCounts).length > 0
      ) {
        createLineChartData(overallCounts, pytdOverallCounts);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [
    calStackChartOptions,
    cytdChartData,
    pytdChartData,
    vioData,
    gvwCategories,
  ]);
  return (
    <Layout>
      <div
        style={{
          overflowY: "auto",
          height: "100%",
          backgroundColor: "#f2f2f2",
        }}
      >
        <div style={{ padding: "0px 0px", marginTop: 10 }}>
          <div
            style={{
              //   display: "flex",
              //   alignItems: "center",
              //   justifyContent: "flex-end",
              //   padding: "0px 12px",
              position: "absolute",
              top: 70,
              right: 20,
              zIndex: 0,
            }}
          >
            <MultiSelectDropdown
              //   label="VIN GVW Class"
              placeholder="All VIN GVWs"
              options={VIN_OPTIONS}
              value={selectedVIN}
              onChange={(val) => {
                setSelectedVIN(val);
                handleDropdown(val);
                getDashboardCarddataTotal(rowDashboardCardData, val);
              }}
              searchable={false}
              selectAllMode="visible"
              placement="auto"
              portal
            />
          </div>
          {hasNewRegistrationCube && (
            <div className="home-header ">
              <div className="home-row" style={{ padding: "0px 12px" }}>
                <div className="home-chart-title">
                  <span className="home-chart-title-text">
                    Performance Metrics - US New Registrations
                  </span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  {/* <div
                  className="home-chart-dropdown"
                  style={{
                    marginLeft: "auto",
                    marginTop: 0,
                    paddingRight: "0px",
                  }}
                >
                  <Dropdown
                    id={"gvw-dropdown"}
                    selectedValue="All VIN GVW Classes"
                    onChange={(val) => {
                      setPerfomanceMetricsCategory(val);
                      getDashboardCarddataTotal(rowDashboardCardData, val);
                    }}
                    options={dropdownOptions}
                  />
                </div> */}

                  <div className="home-chart-title">
                    {mrmoData && mrmoData.label && (
                      <span className="home-chart-title-text">
                        Data as of{" "}
                        {mrmoData.label.replace("Current Month ", "")}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div
                className="home-row"
                style={{ padding: "0px 12px", marginTop: 10 }}
              >
                <DashboardCard
                  title={
                    mrmoData
                      ? `${mrmoData.label || ""}\u00A0 |\u00A0 YoY Difference`
                      : loadingIcon
                  }
                  value={mrmoData ? mrmoData.value : loadingIcon}
                  percent={percentMtdDiff === "" ? loadingIcon : percentMtdDiff}
                />
                <DashboardCard
                  title={mrmo1Data ? mrmo1Data.label : loadingIcon}
                  value={mrmo1Data ? mrmo1Data.value : loadingIcon}
                />
                <DashboardCard
                  title={
                    cytdData
                      ? `${cytdData.label || ""}\u00A0 |\u00A0 YoY Difference`
                      : loadingIcon
                  }
                  value={cytdData ? cytdData.value : loadingIcon}
                  percent={percentYtdDiff === "" ? loadingIcon : percentYtdDiff}
                />
                <DashboardCard
                  title={pytdData ? pytdData.label : loadingIcon}
                  value={pytdData ? pytdData.value : loadingIcon}
                />
              </div>
            </div>
          )}
        </div>

        <div className="chart-home-row" style={{ padding: "0px 12px" }}>
          {hasNewRegistrationCube && (
            <div className="chart-block">
              <div className="home-row gray-bar">
                <div className="home-chart-title">
                  <span className="home-chart-title-text">
                    US New Registrations
                  </span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div className="home-chart-title">
                    {mrmoData && mrmoData.label && (
                      <span className="home-chart-title-text">
                        Data as of{" "}
                        {mrmoData.label.replace("Current Month ", "")}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="line-chart">
                {lineChartOptions ? (
                  <AgCharts options={lineChartOptions} key={1} />
                ) : (
                  <Loading show={true} />
                )}
              </div>
            </div>
          )}
          {hasVioCube && (
            <div className="chart-block">
              <div className="home-row gray-bar" style={{ marginLeft: "8px" }}>
                <div className="home-chart-title">
                  <span className="home-chart-title-text">
                    US Vehicles in Operation
                  </span>
                </div>
                <div className="home-chart-title">
                  {vioAsofData && (
                    <span className="home-chart-title-text">
                      Data as of {vioAsofData.replace("Current Month ", "")}
                    </span>
                  )}
                </div>
              </div>
              <div className="home-chart">
                {stackChartOptions && barGroupChartOptions.length > 0 ? (
                  <>
                    <AgCharts options={stackChartOptions} />
                    {barGroupChartOptions.map(
                      (options, idx) =>
                        options.data[0].category != "Unclassified" && (
                          <AgCharts
                            options={options}
                            key={`bar-group-${idx}`}
                          />
                        ),
                    )}
                  </>
                ) : (
                  <div style={{ width: "100%" }}>
                    <Loading show={true} />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
